// src/utils/validationUtils.js
import { countryList } from './CountryList';
import { MENU_STATUS } from './../config/MenuInfluencerStatus';

const validCountryNames = new Set(
  countryList.map((c) => c.label.toLowerCase()),
);
const validCountryCodes = new Set(
  countryList.map((c) => c.value.toLowerCase()),
);

const REGEX_URL_GENERIC =
  /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\.[\w-]+)*(:\d+)?(\/.*)?$/;
const REGEX_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const REGEX_WHATSAPP_PHONE = /^\+\d{10,15}$/;
const REGEX_USERNAME = /^@?[\w.]+$/;
const REGEX_NAME =
  /^[A-Za-z\u00C0-\u017F0-9\s\u0400-\u04FF\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\uAC00-\uD7AF.,'&()/ -]+$/;
const REGEX_IDIOM = /^[a-z]{2,3}$|^[A-Za-z\s]+$/;

function parseUrlForPlatform(url) {
  if (!url || typeof url !== 'string' || !REGEX_URL_GENERIC.test(url)) {
    return { platform: null, identifier: null };
  }

  try {
    const lowerUrl = url.toLowerCase();
    const parsedUrl = new URL(url.startsWith('http') ? url : `https://` + url);
    const pathParts = parsedUrl.pathname.split('/').filter((p) => p);

    if (lowerUrl.includes('instagram.com')) {
      const ident = pathParts.length > 0 ? pathParts[0] : null;
      if (ident && !['p', 'reel', 'stories', 'explore'].includes(ident)) {
        return { platform: 'instagram', identifier: ident };
      }
    } else if (
      lowerUrl.includes('youtube.com') ||
      lowerUrl.includes('youtu.be')
    ) {
      if (pathParts.length > 0) {
        if (pathParts[0].startsWith('@'))
          return { platform: 'youtube', identifier: pathParts[0] };
        if (pathParts[0] === 'channel' && pathParts[1])
          return { platform: 'youtube', identifier: pathParts[1] };
        if (pathParts[0] === 'c' && pathParts[1])
          return { platform: 'youtube', identifier: pathParts[1] };
      }
      return { platform: 'youtube', identifier: null };
    } else if (lowerUrl.includes('tiktok.com')) {
      if (pathParts.length > 0 && pathParts[0].startsWith('@')) {
        return { platform: 'tiktok', identifier: pathParts[0] };
      }
    } else if (lowerUrl.includes('facebook.com')) {
      const ident = pathParts.length > 0 ? pathParts[0] : null;
      if (ident && !['videos', 'groups', 'events'].includes(ident)) {
        return { platform: 'facebook', identifier: ident };
      }
    } else if (
      lowerUrl.includes('wa.me') ||
      lowerUrl.includes('api.whatsapp.com')
    ) {
      return { platform: 'whatsapp', identifier: null };
    } else if (lowerUrl.includes('t.me')) {
      return {
        platform: 'telegram',
        identifier: pathParts.length > 0 ? pathParts[0] : null,
      };
    }
  } catch (e) {
    return { platform: null, identifier: null };
  }

  return { platform: 'Website', identifier: null };
}

export const validateChannelInputs = (
  contactInfo,
  publishingInfo,
  existingContacts,
  existingPublishing,
) => {
  const errors = {};

  if (contactInfo.identifier) {
    switch (contactInfo.type) {
      case 'gmail':
        if (!REGEX_EMAIL.test(contactInfo.identifier)) {
          errors.identifier =
            'Please enter a valid email address. Check for trailing spaces or invalid characters and try again.';
        }
        break;
      case 'whatsapp':
        if (!REGEX_WHATSAPP_PHONE.test(contactInfo.identifier)) {
          errors.identifier =
            'Enter a valid phone number including country code (e.g., +15551234567) and try again.';
        }
        break;
      case 'instagram':
      case 'telegram':
        if (!REGEX_USERNAME.test(contactInfo.identifier)) {
          errors.identifier =
            'Please enter a valid username (e.g., @username or username) and try again.';
        }
        break;
      case 'facebook':
        if (contactInfo.identifier.length < 3) {
          errors.identifier =
            'Please enter a valid Facebook username or ID and try again.';
        }
        break;
      default:
        break;
    }

    if (contactInfo.link) {
      if (!REGEX_URL_GENERIC.test(contactInfo.link)) {
        errors.contactLink = 'Please enter a valid URL and try again.';
      } else {
        const { platform: detectedPlatform } = parseUrlForPlatform(
          contactInfo.link,
        );
        if (
          detectedPlatform &&
          detectedPlatform !== 'Website' &&
          detectedPlatform !== contactInfo.type
        ) {
          errors.contactLink = `This looks like a ${detectedPlatform} link, but you selected ${contactInfo.type}.`;
        }
      }
    }

    if (
      existingContacts.some(
        (c) =>
          c.socialMedia === contactInfo.type &&
          c.accountIdentifier.toLowerCase() ===
            contactInfo.identifier.toLowerCase(),
      )
    ) {
      errors.identifier = `This ${contactInfo.type} contact already exists for the influencer.`;
    }
  }

  if (publishingInfo.link) {
    if (!REGEX_URL_GENERIC.test(publishingInfo.link)) {
      errors.publishingLink =
        'Please enter a valid URL for the publishing channel and try again.';
    } else {
      const { platform: detectedPlatform } = parseUrlForPlatform(
        publishingInfo.link,
      );
      if (
        detectedPlatform &&
        detectedPlatform !== 'Website' &&
        detectedPlatform !== publishingInfo.type
      ) {
        errors.publishingLink = `This looks like a ${detectedPlatform} link, but you selected ${publishingInfo.type}.`;
      }
    }

    if (
      existingPublishing.some(
        (p) =>
          p.socialMediaLink &&
          p.socialMediaLink.toLowerCase() === publishingInfo.link.toLowerCase(),
      )
    ) {
      errors.publishingLink = `This exact publishing link already exists for the influencer.`;
    }
  }

  if (!contactInfo.identifier && contactInfo.link) {
    errors.identifier =
      'A contact identifier is required if you provide a link.';
  }

  if (
    !publishingInfo.link &&
    (publishingInfo.subscribers || publishingInfo.viewCount)
  ) {
    errors.publishingLink =
      'A media link is required to add subscribers or views.';
  }

  // --- NEW VALIDATION LOGIC ADDED ---
  // Checks if the value, when it exists, is a valid non-negative number.
  if (
    publishingInfo.subscribers &&
    (isNaN(publishingInfo.subscribers) ||
      Number(publishingInfo.subscribers) < 0)
  ) {
    errors.subscribers = 'Subscribers must be a non-negative number.';
  }

  if (
    publishingInfo.viewCount &&
    (isNaN(publishingInfo.viewCount) || Number(publishingInfo.viewCount) < 0)
  ) {
    errors.viewCount = 'Views/Posts must be a non-negative number.';
  }

  return errors;
};

export const validatePartnerInfo = (form) => {
  const errors = {};
  const REGEX_URL = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/;

  if (form.emailPromoCode && !REGEX_EMAIL.test(form.emailPromoCode)) {
    errors.emailPromoCode = 'Please enter a valid email address and try again.';
  }

  if (form.affiliateLink && !REGEX_URL.test(form.affiliateLink)) {
    errors.affiliateLink = 'Please enter a valid URL and try again.';
  }

  if (form.affiliateLink2 && !REGEX_URL.test(form.affiliateLink2)) {
    errors.affiliateLink2 =
      'Please enter a valid URL for the UTM link and try again.';
  }

  if (
    form.revenueUSD &&
    (isNaN(form.revenueUSD) || Number(form.revenueUSD) < 0)
  ) {
    errors.revenueUSD = 'Revenue USD must be a non-negative number.';
  }

  return errors;
};

export const validateHeaderInfo = (form) => {
  const errors = {};

  if (!form.name || form.name.trim() === '') {
    errors.name =
      'Channel name is required. Please enter a name and try again.';
  } else if (form.name.trim().length < 3) {
    errors.name =
      'Channel name must be at least 3 characters. Please fix this and try again.';
  }

  if (!form.status || form.status.trim() === '') {
    errors.status =
      'Status is a required field. Please choose one and try again.';
  }

  const validStatuses = MENU_STATUS;
  if (form.status && !validStatuses.includes(form.status)) {
    errors.status =
      'Invalid status selected. Please choose a valid status and try again.';
  }

  if (form.nickname && form.nickname.trim().length > 50) {
    errors.nickname =
      'Nickname cannot exceed 50 characters. Please reduce the size and try again.';
  } else if (form.nickname && !REGEX_NAME.test(form.nickname)) {
    errors.nickname =
      'Nickname contains invalid characters. Please correct and try again.';
  }

  if (form.country && form.country.trim() !== '') {
    const lowerCountry = form.country.trim().toLowerCase();
    if (
      !validCountryNames.has(lowerCountry) &&
      !validCountryCodes.has(lowerCountry)
    ) {
      errors.country =
        'Please enter a valid country name or 2-letter code and try again.';
    }
  }

  if (form.idiom && form.idiom.trim() !== '') {
    const lowerIdiom = form.idiom.trim().toLowerCase();
    if (!REGEX_IDIOM.test(lowerIdiom)) {
      errors.idiom =
        'Please enter a valid language or 2/3-letter code (e.g., English or en) and try again.';
    }
  }

  if (form.dateLastContact) {
    try {
      const date = new Date(form.dateLastContact);
      const futureDate = new Date();
      futureDate.setFullYear(futureDate.getFullYear() + 5);

      if (isNaN(date.getTime())) {
        errors.dateLastContact =
          'Invalid date format. Please enter a valid date and try again.';
      } else if (date > futureDate) {
        errors.dateLastContact =
          'Date cannot be more than 5 years in the future. Please correct and try again.';
      }
    } catch (e) {
      errors.dateLastContact =
        'Invalid date format. Please enter a valid date and try again.';
    }
  }

  return errors;
};
